import React from 'react'
import PropTypes from 'prop-types'
import {Box, Stack, Text, Button, useMultiStyleConfig} from '@chakra-ui/react'
import {FormattedMessage, FormattedNumber, useIntl} from 'react-intl'
import {Link} from 'react-router-dom'

import {getInfoLabelVariantByOmsStatus, getOrderStatus} from './utils'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import {useCurrency} from '@salesforce/retail-react-app/app/hooks'
import InformativeLabel from '../../../components/informative-label/partials/InformativeLabel'

const OrderListCard = ({order}) => {
    const styles = useMultiStyleConfig('Orders')
    const {formatDate, formatMessage} = useIntl()
    const {locale} = useMultiSite()
    const {localCurrency} = useCurrency()
    const currency = order?.CurrencyISOCode || localCurrency
    const localeId = locale?.alias

    if (!order) return
    // Extract shipment statuses
    const shipmentStatuses = order?.fulfillmentOrder?.map((shipment) => shipment?.Status) || []

    // Pass shipmentStatuses to getOrderStatus
    const {status, message} = getOrderStatus(order?.OrderStatus, false, shipmentStatuses)

    const translatedStatus = message && formatMessage(message)
    return (
        <>
            <Text {...styles.header}>
                <FormattedMessage
                    id="account_order_history.label.order_number"
                    defaultMessage="Order number: {orderNumber}"
                    values={{
                        orderNumber: order?.OrderNumber?.toUpperCase(),
                    }}
                />
                {order?.hasSubscription && ( // verify this field existance
                    <Text as="span" textStyle="body-small" color="text.default" fontWeight="normal">
                        <FormattedMessage
                            id="account_order_history.label.order_has_subscription"
                            defaultMessage="This order contains a subscription"
                        />
                    </Text>
                )}
            </Text>

            <Box {...styles.wrapper}>
                {/* Date */}
                <Box {...styles.firstColumn}>
                    <Stack {...styles.gaps}>
                        <Text {...styles.bodyMedium}>
                            <FormattedMessage
                                defaultMessage="Order date"
                                id="account_order_history.label.ordered"
                            />
                        </Text>
                        <Text {...styles.bodyMedium} fontWeight="bold">
                            <FormattedMessage
                                defaultMessage="{date}"
                                id="account_order_history.label.date"
                                values={{
                                    date: formatDate(new Date(order?.OrderedDate), {
                                        year: 'numeric',
                                        month: '2-digit',
                                        day: '2-digit',
                                    }),
                                }}
                            />
                        </Text>
                    </Stack>
                </Box>
                {/* Products */}
                <Box {...styles.secondColumn}>
                    <Stack {...styles.gaps}>
                        <Text {...styles.bodyMedium}>
                            <FormattedMessage
                                defaultMessage="Products"
                                id="account_order_history.label.num_of_products"
                            />
                        </Text>
                        {/* Sum of all quantities
                         * This is a sum of all quantities of all products in the order
                         */}
                        <Text {...styles.bodyMedium} fontWeight="bold">
                            {order?.OrderTotalQuantity}
                        </Text>
                    </Stack>
                </Box>
                {/* Total */}
                <Box {...styles.firstColumn}>
                    <Stack {...styles.gaps}>
                        <Text {...styles.bodyMedium}>
                            <FormattedMessage
                                defaultMessage="Total"
                                id="account_order_history.label.total"
                            />
                        </Text>
                        <Text {...styles.bodyMedium} fontWeight="bold">
                            <FormattedNumber
                                style="currency"
                                currency={currency}
                                value={order?.GrandTotalAmount}
                                currencyDisplay="narrowSymbol"
                            />
                        </Text>
                    </Stack>
                </Box>
                {/* Status */}
                <Box {...styles.secondColumn}>
                    <Stack {...styles.gaps}>
                        <Text {...styles.bodyMedium}>
                            <FormattedMessage
                                defaultMessage="Status"
                                id="account_order_history.label.status"
                            />
                        </Text>
                        <InformativeLabel
                            label={translatedStatus}
                            width={{
                                base: 'fit-content',
                                md: 'max-content',
                            }}
                            variant={getInfoLabelVariantByOmsStatus(status)}
                        />
                    </Stack>
                </Box>
                {/* Mobile details link */}
                <Box {...styles.detailsContainerMobile}>
                    <Button
                        as={Link}
                        to={`/account/orders/${order?.Id}/${order?.OrderNumber}/${order?.OriginalOrderId}`}
                        variant="link-button"
                        size="base"
                        {...styles.detailsLink}
                    >
                        <FormattedMessage
                            defaultMessage="Details"
                            id="account_order_history.link.details"
                        />
                    </Button>
                </Box>
                {/* Desktop details link */}
                <Box {...styles.detailsContainerDesktop}>
                    <Button
                        as={Link}
                        to={`/${localeId}/account/orders/${order?.Id}/${order?.OrderNumber}/${order?.OriginalOrderId}`}
                        variant="link-button"
                        size="base"
                        {...styles.detailsLink}
                    >
                        <FormattedMessage
                            defaultMessage="Details"
                            id="account_order_history.link.details"
                        />
                    </Button>
                </Box>
            </Box>
        </>
    )
}

OrderListCard.propTypes = {
    order: PropTypes.object,
}

export default OrderListCard
